/**
 * Field, category and output names, as a reader would say them.
 *
 * @module components/shared/diff-labels
 */

import { countLabel } from "@/components/ui/plural";
import type {
  RevisionChangeCategory,
  RevisionChangeKind,
  RevisionFieldChange,
} from "@/server/governance-diff";

const FIELD_LABELS: Record<string, string> = {
  awardStandardIds: "Award standards",
  awardStandardNames: "Award standard names",
  coRequisites: "Co-requisites",
  effortHours: "Effort hours",
  feCredits: "FET credits",
  indicativeContent: "Indicative content",
  isElective: "Elective status",
  kantoBadge: "Kanto badge",
  maximumLearners: "Maximum learners",
  mimloIds: "MIMLO mappings",
  mimlos: "MIMLOs",
  moduleLeadEmail: "Module lead email",
  moduleLeadName: "Module lead name",
  nfqLevel: "NFQ level",
  ploToMimlos: "Programme outcome mappings",
  plos: "Programme learning outcomes",
  programmeCode: "Programme code",
  readingList: "Reading list",
  resourcesRequired: "Resources required",
  schemaVersion: "Schema version",
  staffQualifications: "Staff qualifications",
  totalCredits: "Total credits",
  updatedAt: "Updated at",
  weight: "Assessment weighting",
  weighting: "Assessment weighting",
};

export function humanizeIdentifier(value: string): string {
  return (
    FIELD_LABELS[value] ??
    value
      .replaceAll("_", " ")
      .replaceAll("-", " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (character) => character.toUpperCase())
  );
}

export function humanizeRevisionPath(path: string): string {
  return path
    .split(".")
    .map((segment) => {
      const field = segment.split("[", 1)[0] ?? segment;
      const identifiers = [...segment.matchAll(/\[([^\]]+)]/g)].map((match) => match[1]);
      return [
        humanizeIdentifier(field),
        ...identifiers.map((identifier) => `(${identifier})`),
      ].join(" ");
    })
    .join(" › ");
}

const CATEGORY_LABELS: Record<RevisionChangeCategory, string> = {
  identity: "Programme identity",
  narrative: "Programme details",
  outcomes: "Learning outcomes",
  modules: "Modules",
  "credits-effort": "Credits and effort",
  assessments: "Assessments",
  mappings: "Traceability mapping",
};

const OUTPUT_LABELS: Record<string, string> = {
  "programme-descriptor": "Programme descriptor",
  "programme-schedule": "Programme schedule",
  "appendix-1-evidence": "Appendix 1 evidence",
};

const TECHNICAL_ROOT_FIELDS = new Set(["id", "kantoBadge", "mode", "schemaVersion", "updatedAt"]);

export function revisionCategoryLabel(category: RevisionChangeCategory): string {
  return CATEGORY_LABELS[category];
}

export function derivedOutputLabel(output: string): string {
  return OUTPUT_LABELS[output] ?? humanizeIdentifier(output);
}

export function isTechnicalRevisionChange(change: RevisionFieldChange): boolean {
  const root = change.path.split(/[.[]/, 1)[0] ?? "";
  return TECHNICAL_ROOT_FIELDS.has(root);
}

const KIND_LABELS: Record<RevisionChangeKind, string> = {
  added: "Added",
  removed: "Removed",
  changed: "Changed",
};

export function revisionChangeKindLabel(kind: RevisionChangeKind): string {
  return KIND_LABELS[kind];
}

/** "2 changes in Modules and Assessments", areas in the order they first change. */
export function revisionChangeSummary(changes: readonly RevisionFieldChange[]): string {
  const areas = [...new Set(changes.map((change) => revisionCategoryLabel(change.category)))];
  const joined =
    areas.length < 2
      ? (areas[0] ?? "")
      : `${areas.slice(0, -1).join(", ")} and ${areas[areas.length - 1]}`;
  return areas.length ? `${countLabel(changes.length, "change")} in ${joined}` : "No changes";
}
